<div class="fsr-dashboard p-4">
  <!-- Loading State -->
  <div *ngIf="loading" class="flex items-center justify-center min-h-screen">
    <div class="text-center">
      <p-progressSpinner styleClass="w-16 h-16" strokeWidth="3" animationDuration="1s"></p-progressSpinner>
      <p class="text-gray-600 mt-4 text-lg" i18n>Loading your dashboard...</p>
    </div>
  </div>

  <!-- Main Dashboard Content -->
  <div *ngIf="!loading">
    <!-- Enhanced Header with User Profile -->
    <div class="flex flex-col lg:flex-row lg:justify-between lg:items-center mb-6 gap-4">
      <div class="flex items-center gap-4">
        <div class="relative">
          <p-avatar [label]="getUserInitials()" size="large" styleClass="bg-blue-500 text-white text-lg font-semibold">
          </p-avatar>
          <div class="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 border-2 border-white rounded-full"></div>
        </div>
        <div>
          <h1 class="text-2xl lg:text-3xl font-bold text-gray-900 mb-1" i18n>Welcome, {{ getCurrentUserName() }}</h1>
          <div class="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-gray-600">
            <span class="flex items-center gap-1" i18n>
              <i class="pi pi-briefcase text-xs"></i>
              Field Sales Representative
            </span>
            <span class="flex items-center gap-1" *ngIf="getTerritoryCount() > 0" i18n>
              <i class="pi pi-map text-xs"></i>
              {{ getTerritoryCount() }} Territor{{ getTerritoryDisplayText() }}
            </span>
            <span class="flex items-center gap-1" *ngIf="getTodayScheduleCount() > 0" i18n>
              <i class="pi pi-clock text-xs"></i>
              {{ getTodayScheduleCount() }} Task{{ getTodayScheduleCount() !== 1 ? 's' : '' }} Today
            </span>
          </div>
          <!-- Date Range Display -->
          <div *ngIf="dateFilterDescription" class="mt-2 flex items-center gap-2">
            <div class="flex items-center gap-2 px-3 py-1.5 rounded-full border" 
                 [ngClass]="{'bg-blue-50 border-blue-200': isDateFilterActive, 'bg-gray-50 border-gray-200': !isDateFilterActive}">
              <i class="pi pi-calendar text-sm" [ngClass]="{'text-blue-500': isDateFilterActive, 'text-gray-500': !isDateFilterActive}"></i>
              <span class="text-sm font-medium" [ngClass]="{'text-blue-700': isDateFilterActive, 'text-gray-600': !isDateFilterActive}">{{ dateFilterDescription }}</span>
              <p-button *ngIf="isDateFilterActive" icon="pi pi-times" [text]="true" size="small" styleClass="p-0 h-auto w-auto" (onClick)="clearDateFilter()" [pTooltip]="'Clear Filter'" i18n-pTooltip></p-button>
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
        <!-- Date Range Filter -->
        <div class="flex items-center flex-col md:flex-row gap-2 p-3 bg-white rounded-lg " [class.border-blue-500]="isDateFilterActive" [class.bg-blue-50]="isDateFilterActive">
          <i class="pi pi-calendar text-gray-500"></i>
          <p-select [(ngModel)]="dateFilter.period" [options]="periodOptions" optionLabel="label" optionValue="value" (onChange)="onPeriodChange()" styleClass="w-full sm:w-auto" [style]="{'min-width': '140px'}" placeholder="Select Period" i18n-placeholder i18n-placeholder>
          </p-select>

          <div *ngIf="dateFilter.period === 'custom'" class="flex flex-col md:flex-row items-center gap-2 md:ml-2">
            <p-datePicker [(ngModel)]="dateFilter.startDate" placeholder="Start Date" i18n-placeholder i18n-placeholder (onSelect)="onDateRangeChange()" showIcon="true" dateFormat="mm/dd/yy">
            </p-datePicker>
            <span class="text-gray-400">-</span>
            <p-datePicker [(ngModel)]="dateFilter.endDate" placeholder="End Date" i18n-placeholder i18n-placeholder (onSelect)="onDateRangeChange()" showIcon="true" dateFormat="mm/dd/yy">
            </p-datePicker>
          </div>

          <p-button *ngIf="isDateFilterActive" icon="pi pi-times" [text]="true" size="small" (onClick)="clearDateFilter()" [pTooltip]="'Clear Filter'" i18n-pTooltip>
          </p-button>
        </div>

        <p-button appGlobalLoadingDisable label="Lookup Customer" icon="pi pi-search" [outlined]="true" (onClick)="navigateToCustomerLookup()" i18n-label></p-button>
      </div>
    </div>

    <!-- Rejected Contracts Alert Card - Moved to Top for Visibility -->
    <div *ngIf="!loading && dashboardData && getRejectedContractsCount() > 0" class="mb-8">
      <p-card class="border-2 border-red-500 bg-gradient-to-r from-red-50 to-orange-50 shadow-lg">
        <div class="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div class="flex items-start gap-4 flex-1">
            <div class="flex-shrink-0">
              <div class="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center">
                <i class="pi pi-exclamation-triangle text-white text-2xl"></i>
              </div>
            </div>
            <div class="flex-1">
              <h3 class="text-xl font-bold text-red-900 mb-2" i18n>Rejected Contracts Require Attention</h3>
              <p class="text-gray-700 mb-3" i18n>
                You have <strong>{{ getRejectedContractsCount() }}</strong> rejected contract{{ getRejectedContractsCount() !== 1 ? 's' : '' }} that need to be reviewed and resubmitted.
              </p>
              <div class="space-y-2">
                <div *ngFor="let contract of getRejectedContracts().slice(0, 3)" class="flex items-center justify-between p-2 bg-white rounded border border-red-200">
                  <div class="flex-1">
                    <span class="font-medium text-gray-900">{{ contract.customerName || 'Unknown Customer' }}</span>
                    <span class="text-sm text-gray-600 ml-2">- {{ contract._id.slice(-8) }}</span>
                  </div>
                  <p-button label="View & Resubmit" size="small" icon="pi pi-eye" (onClick)="onContractAction('view', contract._id)" i18n-label>
                  </p-button>
                </div>
                <div *ngIf="getRejectedContractsCount() > 3" class="text-sm text-gray-600 text-center pt-2">
                  <span i18n>And {{ getRejectedContractsCount() - 3 }} more rejected contract{{ getRejectedContractsCount() - 3 !== 1 ? 's' : '' }}...</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </p-card>
    </div>

    <!-- Terminated Contracts Alert Card - Moved to Top for Visibility -->
    <div *ngIf="!loading && dashboardData && getTerminatedContractsCount() > 0" class="mb-8">
      <p-card class="border-2 border-red-500 bg-gradient-to-r from-red-50 to-orange-50 shadow-lg">
        <div class="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div class="flex items-start gap-4 flex-1">
            <div class="flex-shrink-0">
              <div class="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center">
                <i class="pi pi-exclamation-triangle text-white text-2xl"></i>
              </div>
            </div>
            <div class="flex-1">
              <h3 class="text-xl font-bold text-red-900 mb-2" i18n>Terminated Contracts Require Attention</h3>
              <p class="text-gray-700 mb-3" i18n>
                You have <strong>{{ getTerminatedContractsCount() }}</strong> terminated contract{{ getTerminatedContractsCount() !== 1 ? 's' : '' }} that need to be reviewed.
              </p>
              <div class="space-y-2">
                <div *ngFor="let contract of getTerminatedContracts().slice(0, 3)" class="flex items-center justify-between p-2 bg-white rounded border border-red-200">
                  <div class="flex-1">
                    <span class="font-medium text-gray-900">{{ contract.customerName || 'Unknown Customer' }}</span>
                    <span class="text-sm text-gray-600 ml-2">- {{ contract._id.slice(-8) }}</span>
                  </div>
                  <p-button label="View" size="small" icon="pi pi-eye" (onClick)="onContractAction('view', contract._id)" i18n-label>
                  </p-button>
                </div>
                <div *ngIf="getTerminatedContractsCount() > 3" class="text-sm text-gray-600 text-center pt-2">
                  <span i18n>And {{ getTerminatedContractsCount() - 3 }} more terminated contract{{ getTerminatedContractsCount() - 3 !== 1 ? 's' : '' }}...</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </p-card>
    </div>

    <!-- KPI Cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
      <!-- Loading Skeletons -->
      <div *ngIf="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 col-span-full">
        <p-card *ngFor="let item of [1,2,3,4,5]" class="kpi-card">
          <div class="flex items-center justify-between">
            <div class="w-full">
              <div class="flex items-center gap-2 mb-2">
                <p-skeleton shape="circle" size="1.5rem"></p-skeleton>
                <p-skeleton width="8rem" height="1rem"></p-skeleton>
              </div>
              <p-skeleton width="4rem" height="2rem" styleClass="mb-2"></p-skeleton>
              <p-skeleton width="6rem" height="0.875rem"></p-skeleton>
            </div>
          </div>
        </p-card>
      </div>

      <!-- Actual KPI Cards -->
      <div *ngIf="!loading && dashboardData" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 col-span-full">
        <!-- My Reservations -->
        <p-card class="relative" [class.filtered-highlight]="isDateFilterActive" i18n-pTooltip tooltipPosition="top">
          <div class="flex items-center justify-between">
            <div>
              <div class="flex items-center gap-2 mb-2">
                <i class="pi pi-calendar-plus text-blue-500 text-xl" [pTooltip]="'Your reservation count'" i18n-pTooltip></i>
                <span class="text-sm font-medium text-gray-600" i18n>My Reservations</span>
              </div>
              <div class="text-3xl font-bold text-gray-900">{{ dashboardData.myStats.myReservations }}</div>
              <div class="text-sm" [class]="getUrgencyClass()">
                {{ getUrgentReservationsText() }}
              </div>
            </div>
            <div *ngIf="getUrgentReservationsCount() > 0" class="text-right">
              <i class="pi pi-exclamation-triangle text-red-500 text-lg" [pTooltip]="'You have reservations expiring today that need attention!'" i18n-pTooltip tooltipPosition="top"></i>
            </div>
          </div>
        </p-card>

        <!-- Total Customers -->
        <p-card class="kpi-card cursor-pointer relative" [class.filtered-highlight]="isDateFilterActive" (click)="navigateToCustomers()" [pTooltip]="'View all your customers and their information. Click to navigate to customer management.'" i18n-pTooltip tooltipPosition="top">
          <div class="absolute top-3 right-3 z-10" [pTooltip]="'Clickable'" i18n-pTooltip tooltipPosition="top">
            <div class="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center border border-blue-300 shadow-sm hover:bg-blue-200 transition-colors cursor-default">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#2563EB" xmlns="http://www.w3.org/2000/svg">
                <path d="M13 1.07V9h7c0-4.08-3.05-7.44-7-7.93zM4 15c0 4.42 3.58 8 8 8s8-3.58 8-8v-4H4v4zm7-13.93C7.05 1.56 4 4.92 4 9h7V1.07z"/>
              </svg>
            </div>
          </div>
          <div class="flex items-center justify-between">
            <div>
              <div class="flex items-center gap-2 mb-2">
                <i class="pi pi-users text-green-500 text-xl" [pTooltip]="'Your customer count'" i18n-pTooltip></i>
                <span class="text-sm font-medium text-gray-600" i18n>Total Customers</span>
              </div>
              <div class="text-3xl font-bold text-gray-900">{{ dashboardData?.myCustomers?.length || 0 }}</div>
              <div class="text-sm text-gray-500" i18n>Active customers</div>
            </div>
          </div>
        </p-card>

        <!-- Contracts Created -->
        <p-card class="cursor-default relative" [class.filtered-highlight]="isDateFilterActive" i18n-pTooltip >
          <div class="flex items-center justify-between">
            <div>
              <div class="flex items-center gap-2 mb-2">
                <i class="pi pi-file-edit text-purple-500 text-xl" [pTooltip]="'Your active contract count and value'" i18n-pTooltip></i>
                <span class="text-sm font-medium text-gray-600" i18n>Active Contracts</span>
              </div>
              <div class="text-3xl font-bold text-gray-900">{{ dashboardData.myStats.myContracts }}</div>
              <div class="text-sm text-gray-500" [pTooltip]="'Total value of all your active contracts'" i18n-pTooltip tooltipPosition="top">{{ formatCurrency(dashboardData.myStats.myContractValue) }}</div>
            </div>
          </div>
        </p-card>

        <!-- Conversion Rate -->
        <p-card class="kpi-card relative" [class.filtered-highlight]="isDateFilterActive" [pTooltip]="'Your success rate in converting reservations to contracts. Higher percentages indicate better performance.'" i18n-pTooltip tooltipPosition="top">
          <div class="flex items-center justify-between">
            <div>
              <div class="flex items-center gap-2 mb-2">
                <i class="pi pi-chart-line text-orange-500 text-xl" [pTooltip]="'Your conversion rate percentage'" i18n-pTooltip></i>
                <span class="text-sm font-medium text-gray-600" i18n>Conversion Rate</span>
              </div>
              <div class="text-3xl font-bold text-gray-900">{{ dashboardData.myStats.myConversionRate }}%</div>
              <div class="text-sm text-gray-500" i18n>This period</div>
            </div>
          </div>
        </p-card>

        <!-- Today's Schedule Count -->
        <p-card class="kpi-card relative" [pTooltip]="'Number of appointments and interactions scheduled for today. Click items in Today\'s Schedule for details.'" i18n-pTooltip tooltipPosition="top">
          <div class="flex items-center justify-between">
            <div>
              <div class="flex items-center gap-2 mb-2">
                <i class="pi pi-clock text-indigo-500 text-xl" [pTooltip]="'Today\'s scheduled items'" i18n-pTooltip></i>
                <span class="text-sm font-medium text-gray-600" i18n>Today's Schedule</span>
              </div>
              <div class="text-3xl font-bold text-gray-900">{{ getTodayScheduleCount() }}</div>
              <div class="text-sm text-gray-500" i18n>Upcoming appointments</div>
            </div>
          </div>
        </p-card>
      </div>
    </div>

    <!-- Quick Stats Overview -->
    <!-- <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" *ngIf="dashboardData">
    <p-card class="quick-stat-card">
      <div class="text-center">
        <div class="text-2xl font-bold text-blue-600 mb-1">{{ dashboardData?.myCustomers?.length || 0 }}</div>
        <div class="text-sm text-gray-600">Active Customers</div>
        <div class="text-xs text-green-600 mt-1" *ngIf="getCustomerStatusSummary().newThisMonth > 0">
          +{{ getCustomerStatusSummary().newThisMonth }} this month
        </div>
      </div>
    </p-card>
    
    <p-card class="quick-stat-card">
      <div class="text-center">
        <div class="text-2xl font-bold text-orange-600 mb-1">{{ getReservationStatusSummary().pending }}</div>
        <div class="text-sm text-gray-600">Pending Actions</div>
        <div class="text-xs text-red-600 mt-1" *ngIf="getReservationStatusSummary().expiringSoon > 0">
          {{ getReservationStatusSummary().expiringSoon }} expiring soon
        </div>
      </div>
    </p-card>
    
    <p-card class="quick-stat-card">
      <div class="text-center">
        <div class="text-2xl font-bold text-green-600 mb-1">{{ formatCurrency(getTotalContractValue()) }}</div>
        <div class="text-sm text-gray-600">Contract Value</div>
        <div class="text-xs text-blue-600 mt-1">{{ getDisplayedContracts().length }} contracts</div>
      </div>
    </p-card>
    
    <p-card class="quick-stat-card">
      <div class="text-center">
        <div class="text-2xl font-bold text-purple-600 mb-1">{{ dashboardData.myStats.myConversionRate }}%</div>
        <div class="text-sm text-gray-600">Success Rate</div>
        <div class="text-xs text-gray-500 mt-1">This period</div>
      </div>
    </p-card>
  </div> -->

    <!-- Main Content -->
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8" *ngIf="dashboardData">
      <!-- Left Column -->
      <div class="lg:col-span-2 space-y-6">
        <!-- Customer Portfolio Overview -->
        <p-card [class.filtered-highlight]="isDateFilterActive">
          <ng-template pTemplate="header">
            <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center p-4 gap-3">
              <h3 class="text-lg font-semibold" i18n>My Customers</h3>
              <div class="flex flex-col sm:flex-row sm:items-center gap-2">
                <span class="text-sm text-gray-600" i18n>{{ dashboardData?.myCustomers?.length || 0 }} total customers</span>
                <p-button appGlobalLoadingDisable label="View All" [text]="true" size="small" (onClick)="onCustomerAction('viewAll')" [pTooltip]="'View all your customers with detailed filtering and management options'" i18n-label i18n-pTooltip tooltipPosition="top"></p-button>
              </div>
            </div>
          </ng-template>
          <div class="overflow-x-auto">
            <p-table [value]="getDisplayedCustomers()" [tableStyle]="{'min-width': '50rem'}">
              <ng-template pTemplate="header">
                <tr>
                  <th i18n>Business</th>
                  <th i18n>Industry</th>
                  <th i18n>Interest Level</th>
                  <th i18n>Location</th>
                  <th i18n>Added Date</th>

                  <th i18n>Actions</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-customer>
                <tr>
                  <td>
                    <div class="flex items-center gap-2">
                      <i class="pi pi-building text-blue-500"></i>
                      <div>
                        <div class="font-medium">{{ customer.businessName }}</div>
                        <div class="text-sm text-gray-500" i18n>{{ customer.numberOfEmployees }} employees</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <p-tag [value]="customer.industry" severity="info" size="small"></p-tag>
                  </td>
                  <td>
                    <p-tag [value]="customer.interestLevel" [severity]="getInterestLevelSeverity(customer.interestLevel)" size="small">
                    </p-tag>
                  </td>
                  <td>
                    <div class="text-sm">{{ customer.location }}</div>
                  </td>
                  <td>
                    <div class="text-sm">{{ customer.createdAt }}</div>
                  </td>

                  <td>
                    <div class="flex gap-2">
                      <p-button appGlobalLoadingDisable label="View" [text]="true" size="small" (onClick)="onCustomerAction('view', customer._id)" i18n-label></p-button>
                    </div>
                  </td>
                </tr>
              </ng-template>
            </p-table>
          </div>
        </p-card>

        <!-- Recent Activity Timeline -->
        <p-card [class.filtered-highlight]="isDateFilterActive">
          <ng-template pTemplate="header">
            <div class="flex justify-between items-center p-4">
              <h3 class="text-lg font-semibold" i18n>Recent Activity Timeline</h3>
              <div class="flex items-center gap-2">
                <span class="text-sm text-gray-600" i18n>Last 10 interactions</span>
                <p-button appGlobalLoadingDisable label="View All" [text]="true" size="small" (onClick)="onCustomerAction('viewAll')" i18n-label></p-button>
              </div>
            </div>
          </ng-template>
          <div class="activity-timeline p-4">
            <div *ngFor="let interaction of getRecentInteractions()" class="flex items-start gap-4 mb-6 last:mb-0">

              <!-- Timeline Icon -->
              <div class="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center" [class]="getActivityTimelineIconClass(interaction.type)">
                <i [class]="getActivityTimelineIcon(interaction.type)"></i>
              </div>

              <div class="flex-1 min-w-0">
                <div class="flex items-center justify-between mb-1">
                  <h4 class="font-medium text-gray-900">{{ interaction.customerName }}</h4>
                  <div class="text-right">
                    <div class="text-sm text-gray-500">{{ formatDate(interaction.date) }}</div>
                    <div class="text-xs text-gray-400">{{ interaction.time }}</div>
                  </div>
                </div>
                <p class="text-sm text-gray-600 mb-2">{{ getActivityDescription(interaction) }}</p>
                <div class="flex items-center gap-2">
                  <p-tag [value]="interaction.type" [severity]="getInteractionTypeSeverity(interaction.type)" size="small"></p-tag>
                  <p-tag [value]="interaction.status" [severity]="getInteractionStatusSeverity(interaction.status)" size="small"></p-tag>
                </div>
              </div>
            </div>
          </div>
        </p-card>

        <!-- Assigned Territories -->
        <p-card [class.filtered-highlight]="isDateFilterActive">
          <ng-template pTemplate="header">
            <div class="flex justify-between items-center p-4">
              <h3 class="text-lg font-semibold" i18n>Assigned Territories</h3>
              <p-button appGlobalLoadingDisable label="View All On Map" [text]="true" size="small" (onClick)="navigateToTerritoryManagement()" i18n-label></p-button>
            </div>
          </ng-template>
          <div class="flex flex-wrap gap-2 p-4">
            <p-tag *ngFor="let territory of getDisplayedTerritories()" [value]="territory.name" [severity]="getStatusSeverity(territory.status)">
            </p-tag>
            <p-tag *ngIf="getExtraTerritoriesCount() > 0" [value]="'+' + getExtraTerritoriesCount() + ' more'" severity="info">
            </p-tag>
          </div>
        </p-card>

        <!-- Weekly Performance Chart -->
        <p-card [class.filtered-highlight]="isDateFilterActive">
          <ng-template pTemplate="header">
            <div class="p-4">
              <div class="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2 gap-3">
                <div>
                  <h3 class="text-lg font-semibold" i18n>Weekly Performance | Reservations vs Contracts</h3>
                  <p class="text-sm text-gray-600" i18n>Compare daily reservation creation vs contract conversion over the past week</p>
                </div>
                <div class="text-left sm:text-right">
                  <div class="text-2xl font-bold text-green-600">{{ getWeeklyConversionRate() }}%</div>
                  <div class="text-xs text-gray-500" i18n>Conversion Rate</div>
                </div>
              </div>
            </div>
          </ng-template>
          <div class="chart-container" style="height: 250px; min-height: 200px;">
            <p-chart type="line" [data]="dashboardData.weeklyPerformance" [options]="weeklyPerformanceOptions"></p-chart>
          </div>
        </p-card>
      </div>

      <!-- Right Column -->
      <div class="space-y-6">
        <!-- My Reservations Overview -->
        <p-card [class.filtered-highlight]="isDateFilterActive">
          <ng-template pTemplate="header">
            <div class="flex justify-between items-center p-4">
              <h3 class="text-lg font-semibold" i18n>My Active Reservations</h3>
              <!-- <p-button appGlobalLoadingDisable label="View All" [text]="true" size="small" (onClick)="onReservationAction('viewAll')" i18n-label></p-button> -->
            </div>
          </ng-template>
          <div class="space-y-3 p-4" *ngIf="dashboardData.myReservations && dashboardData.myReservations.length > 0; else noReservations">
            <div *ngFor="let reservation of dashboardData.myReservations.slice(0, 5)" class="p-3 border rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer" [class.border-red-200]="isReservationExpiringSoon(reservation.expiryDate)" [class.bg-red-50]="isReservationExpiringSoon(reservation.expiryDate)" (click)="onReservationAction('view', reservation._id)">
              <div class="flex md:flex-row flex-col justify-between items-start">
                <div class="flex-1">
                  <div class="font-medium text-gray-900">{{ reservation.customerName }}</div>
                  <div class="text-sm text-gray-600 mt-1 flex items-center gap-2">
                    <span i18n>{{ reservation.productCount }} product{{ reservation.productCount !== 1 ? 's' : '' }}</span>
                    <span class="text-gray-400">•</span>
                    <span [class.text-red-600]="isReservationExpiringSoon(reservation.expiryDate)">
                      {{ formatDate(reservation.expiryDate) }}
                    </span>
                  </div>
                </div>
                <div class="flex flex-col items-end gap-1 mt-2 md:mt-0 ">
                  <p-tag [value]="reservation.status" [severity]="getReservationStatusSeverity(reservation.status)" size="small">
                  </p-tag>
                  <span *ngIf="isReservationExpiringSoon(reservation.expiryDate)" class="text-xs text-red-600 font-medium" i18n>
                    Expires Soon
                  </span>
                </div>
              </div>
            </div>
          </div>
          <ng-template #noReservations>
            <div class="p-4 text-center text-gray-500">
              <i class="pi pi-calendar text-4xl mb-2"></i>
              <div i18n>No active reservations</div>
            </div>
          </ng-template>
        </p-card>

        <!-- Today's Schedule -->
        <p-card>
          <ng-template pTemplate="header">
            <div class="flex flex-col sm:flex-row sm:justify-between sm:items-center p-4 gap-3">
              <h3 class="text-lg font-semibold" i18n>Today's Schedule</h3>
              <div class="flex flex-col sm:flex-row sm:items-center gap-2">
                <span class="text-sm text-gray-600" i18n>Upcoming interactions</span>
                <p-button appGlobalLoadingDisable label="View Calendar" [text]="true" size="small" (onClick)="navigateToCalendar()" i18n-label></p-button>
              </div>
            </div>
          </ng-template>
          <div class="space-y-4 p-4" *ngIf="getTodayScheduleItems().length > 0; else noSchedule">
            <div *ngFor="let scheduleItem of getTodayScheduleItems()" class="p-4 border-l-4 rounded-r-lg bg-white shadow-sm  cursor-default" [class]="getScheduleTypeColor(scheduleItem.type)" >
              <div class="flex justify-between items-start">
                <div class="flex-1">
                  <div class="font-medium text-gray-900">{{ scheduleItem.customer }}</div>
                  <div class="flex items-center gap-2 mt-2">
                    <i class="pi pi-clock text-gray-500 text-sm"></i>
                    <span class="text-sm text-gray-600">{{ scheduleItem.time }}</span>
                  </div>
                  <div class="mt-2">
                    <p-tag [value]="scheduleItem.type" [severity]="getInteractionTypeSeverity(scheduleItem.type)" size="small"></p-tag>
                    <span class="ml-2 text-xs font-medium text-blue-600">TODAY</span>
                  </div>
                </div>
                <div class="text-right">
                  <span class="text-xs font-medium" [class]="getSchedulePriorityColor(scheduleItem.priority || 'Medium')">
                    {{ scheduleItem.priority || 'Medium' }}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <ng-template #noSchedule>
            <div class="p-4 text-center text-gray-500">
              <i class="pi pi-calendar text-4xl mb-2"></i>
              <div i18n>No upcoming interactions</div>
            </div>
          </ng-template>
        </p-card>

        <!-- My Performance Insights -->
        <!-- <p-card>
        <ng-template pTemplate="header">
          <div class="p-4">
            <h3 class="text-lg font-semibold">Performance Insights</h3>
          </div>
        </ng-template>
        <div class="p-4 space-y-4">
          <div class="flex items-center justify-between p-3 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg">
            <div>
              <div class="font-medium text-blue-900">Conversion Rate</div>
              <div class="text-sm text-blue-700">{{ dashboardData.myStats.myConversionRate }}% success rate</div>
            </div>
            <div class="text-2xl font-bold text-blue-600">
              {{ dashboardData.myStats.myConversionRate }}%
            </div>
          </div>
          
          <div class="flex items-center justify-between p-3 bg-gradient-to-r from-green-50 to-green-100 rounded-lg">
            <div>
              <div class="font-medium text-green-900">Total Value</div>
              <div class="text-sm text-green-700">{{ getDisplayedContracts().length }} contracts</div>
            </div>
            <div class="text-xl font-bold text-green-600">
              {{ formatCurrency(getTotalContractValue()) }}
            </div>
          </div>
          
          <div class="flex items-center justify-between p-3 bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg">
            <div>
              <div class="font-medium text-purple-900">Territory Coverage</div>
              <div class="text-sm text-purple-700">{{ getActiveTerritoryCount() }} active territories</div>
            </div>
            <div class="text-xl font-bold text-purple-600">
              {{ getActiveTerritoryCount() }}
            </div>
          </div>
          
          <div class="flex items-center justify-between p-3 bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg">
            <div>
              <div class="font-medium text-orange-900">Customer Base</div>
              <div class="text-sm text-orange-700">Active relationships</div>
            </div>
            <div class="text-xl font-bold text-orange-600">
              {{ dashboardData?.myCustomers?.length || 0 }}
            </div>
          </div>
        </div>
      </p-card> -->

        <!-- Recent Contracts -->
        <p-card [class.filtered-highlight]="isDateFilterActive">
          <ng-template pTemplate="header">
            <div class="flex justify-between items-center p-4">
              <h3 class="text-lg font-semibold" i18n>Recent Contracts</h3>
              <!-- <p-button appGlobalLoadingDisable label="View All" [text]="true" size="small" (onClick)="onContractAction('viewAll')" i18n-label></p-button> -->
            </div>
          </ng-template>
          <div class="space-y-3 p-4" *ngIf="getDisplayedContracts().length > 0; else noContracts">
            <div *ngFor="let contract of getDisplayedContracts().slice(0, 3)" class="p-3 border rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer" (click)="onCustomerAction('view', contract.customerId, 'contracts')">
              <div class="flex justify-between items-start">
                <div class="flex-1">
                  <div class="font-medium text-gray-900">{{ contract.customerName }}</div>
                  <div class="text-sm text-gray-600 mt-1">
                    <span class="font-medium">{{ formatCurrency(contract.contractValue) }}</span>
                  </div>
                  <div class="text-xs text-gray-500 mt-1">
                    {{ formatDate(contract.startDate) }} - {{ formatDate(contract.endDate) }}
                  </div>
                </div>
                <p-tag [value]="contract.status" [severity]="getContractStatusSeverity(contract.status)" size="small">
                </p-tag>
              </div>
            </div>
          </div>
          <ng-template #noContracts>
            <div class="p-4 text-center text-gray-500">
              <i class="pi pi-file-edit text-4xl mb-2"></i>
              <div i18n>No contracts created yet</div>
            </div>
          </ng-template>
        </p-card>
      </div>
    </div>
  </div> <!-- End of main dashboard content -->
</div>